/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation } from '@apollo/client/react/hooks';
import { OrderEvent } from '@oolio-group/domain';
import { useCallback, useMemo } from 'react';
import { SYNC_ORDER_EVENTS } from '../../graphql/syncEvents';
import { dedupeOrderEvents } from '@oolio-group/order-helper';
import { localComms, isLocalCommsEligible } from '../../utils/localComms';
import { networkStatusSubject } from '../connectivity/useNetworkListener';
import Sentry from '../../utils/Sentry/Sentry';

const RETRY_DELAY_MS = 5000;
const SENTRY_ALERT_AFTER_ATTEMPTS = 5;

type SyncEventsMutate = (options: {
  variables: { input: OrderEvent[] };
}) => Promise<any>;

// Resolves as soon as either the network comes back or the delay elapses,
// whichever happens first — so a retry fires promptly on reconnect instead
// of waiting out the full delay while offline.
function waitForReconnectOrDelay(delayMs: number): Promise<void> {
  return new Promise(resolve => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      subscription.unsubscribe();
      resolve();
    };
    const timer = setTimeout(finish, delayMs);
    const subscription = networkStatusSubject.subscribe(isConnected => {
      if (isConnected === true) finish();
    });
  });
}

// Payment events (completion/cancellation) synced via GQL must not be
// silently dropped — Apollo's own RetryLink only retries once with a
// ~300ms delay, which doesn't cover a real offline period. Retry
// indefinitely every RETRY_DELAY_MS instead of giving up.
async function syncEventsWithRetry(
  syncEvents: SyncEventsMutate,
  uniqueEvents: OrderEvent[],
  attempt = 0,
): Promise<any> {
  try {
    return await syncEvents({ variables: { input: uniqueEvents } });
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'sync-order-events',
      message: `syncEvents mutation failed, retrying (attempt ${attempt + 1})`,
      level: 'warning',
      data: { error: String(error) },
    });
    if (
      attempt + 1 === SENTRY_ALERT_AFTER_ATTEMPTS ||
      (attempt + 1 > SENTRY_ALERT_AFTER_ATTEMPTS && (attempt + 1) % 10 === 0)
    ) {
      Sentry.captureMessage('syncEvents mutation retrying repeatedly', {
        level: 'error',
        extra: { attempts: attempt + 1 },
      });
    }
    await waitForReconnectOrDelay(RETRY_DELAY_MS);
    return syncEventsWithRetry(syncEvents, uniqueEvents, attempt + 1);
  }
}

// Mirrors the backend's non-null EventInput fields. Sending an event missing
// any of these fails GraphQL variable validation for the whole batch.
function hasRequiredFields(event: OrderEvent): boolean {
  return Boolean(
    event.organizationId && event.venueId && event.storeId && event.deviceId,
  );
}

export function useSyncOrderEvents(
  postCompleteSync?: () => void,
  postErrorSyncPost?: () => void,
) {
  const onErrorSync = useCallback(() => {
    postErrorSyncPost && postErrorSyncPost();
  }, [postErrorSyncPost]);

  const onCompletedSync = useCallback(
    (data: any) => {
      if (data?.syncEvents) {
        postCompleteSync && postCompleteSync();
      }
    },
    [postCompleteSync],
  );

  const [syncEvents] = useMutation(SYNC_ORDER_EVENTS, {
    onError: onErrorSync,
    onCompleted: onCompletedSync,
  });

  const syncOrderEvents = useCallback(
    (events: OrderEvent[]) => {
      const uniqueEvents = dedupeOrderEvents(events);
      if (!uniqueEvents.every(hasRequiredFields)) {
        onErrorSync();
        return;
      }
      if (isLocalCommsEligible()) {
        return localComms.addEvents(uniqueEvents);
      }
      return syncEventsWithRetry(syncEvents, uniqueEvents);
    },
    [syncEvents, onErrorSync],
  );

  return useMemo(
    () => ({
      syncOrderEvents: syncOrderEvents,
    }),
    [syncOrderEvents],
  );
}
